import { useState, useEffect, useCallback } from 'react';
import { SidebarSimple } from '@phosphor-icons/react';
import { chatService } from '../../../services/chat.service';
import { useSessions } from '../hooks/useSessions';
import { useChatStream } from '../hooks/useChatStream';
import { SessionSidebar } from './SessionSidebar';
import { MessageList, EmptyState } from './MessageList';
import { ChatInput } from './ChatInput';

export function ChatPage() {
  const { sessions, fetchSessions, createSession, deleteSession } =
    useSessions();

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [llmProvider, setLlmProvider] = useState<'nvidia' | 'llama' | 'deepseek' | 'claude'>('nvidia');

  const { messages, isStreaming, send, stop, loadMessages, clearMessages } = useChatStream({
    sessionId: activeSessionId,
    onTitleReady: () => fetchSessions(), // Refresh sidebar after title is generated
  });

  // Load sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Load a session's messages when switching
  const handleSelectSession = useCallback(
    async (sessionId: string) => {
      if (sessionId === activeSessionId) return;
      setActiveSessionId(sessionId);
      clearMessages();
      try {
        const detail = await chatService.loadSession(sessionId);
        loadMessages(detail.messages);
      } catch {
        // session load failed
      }
    },
    [activeSessionId, clearMessages, loadMessages]
  );

  // Create a new session
  const handleNewSession = useCallback(async () => {
    const session = await createSession();
    setActiveSessionId(session.session_id);
    clearMessages();
  }, [createSession, clearMessages]);

  // Delete session
  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      await deleteSession(sessionId);
      if (sessionId === activeSessionId) {
        setActiveSessionId(null);
        clearMessages();
      }
    },
    [activeSessionId, deleteSession, clearMessages]
  );

  // Send a message
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    setIsSidebarOpen(false);

    let sid = activeSessionId;
    if (!sid) {
      const session = await createSession();
      sid = session.session_id;
      setActiveSessionId(sid);
    }
    send(text, sid, llmProvider);
  }, [input, isStreaming, activeSessionId, createSession, send, llmProvider]);


  return (
    <div className="flex h-full w-full bg-transparent relative z-10">
      <SessionSidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelect={handleSelectSession}
        onNew={handleNewSession}
        onDelete={handleDeleteSession}
        isOpen={isSidebarOpen}
      />

      {/* Chat Window */}
      <div className="flex flex-col flex-1 min-w-0 shadow-[-4px_0_24px_rgba(0,0,0,0.02)] relative overflow-hidden">
        {/* Click-outside overlay */}
        {isSidebarOpen && (
          <div 
            className="absolute inset-0 z-50 bg-transparent cursor-pointer"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        
        {/* Header */}
        <div className="px-6 py-4 shrink-0 shadow-sm border-b border-slate-200/50 backdrop-blur-md z-10 flex items-center justify-between relative">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1.5 -ml-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <SidebarSimple size={20} weight="regular" />
            </button>
            <h1 className="font-outfit font-semibold text-slate-800 text-lg tracking-tight">
              {sessions.find((s) => s.session_id === activeSessionId)?.title ?? 'LinkAlum Assistant'}
            </h1>
          </div>
          <div className="flex items-center">
            <select
              value={llmProvider}
              onChange={(e) => setLlmProvider(e.target.value as 'nvidia' | 'llama' | 'deepseek' | 'claude')}
              className="text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer transition-all hover:bg-slate-100 shadow-sm"
            >
              <option value="nvidia">NVIDIA Nemotron</option>
              <option value="llama">Llama 3.3 (70B)</option>
              <option value="deepseek">DeepSeek Flash</option>
              <option value="claude">Claude 3.5 Sonnet</option>
            </select>
          </div>
        </div>

        {/* Messages or Empty State */}
        {messages.length === 0 ? (
          <div className="flex flex-col flex-1 items-center justify-center p-4">
            <EmptyState />
            <div className="w-full">
              <ChatInput
                value={input}
                onChange={setInput}
                onSend={handleSend}
                onStop={stop}
                isStreaming={isStreaming}
              />
            </div>
          </div>
        ) : (
          <>
            <MessageList messages={messages} />
            <div className="shrink-0">
              <ChatInput
                value={input}
                onChange={setInput}
                onSend={handleSend}
                onStop={stop}
                isStreaming={isStreaming}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
